import React, { useEffect, useState, useCallback } from 'react';
import { formatDateTime } from '../utils';
import { useStaffContext } from '../contexts/StaffContext';

const ChangeHistoryPanel = ({ nodeId, apiService, onRevert }) => {
  const { getDriDisplayName } = useStaffContext();
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmChange, setConfirmChange] = useState(null);
  const [revertingId, setRevertingId] = useState(null);

  const parseValue = (value) => {
    if (value === null || value === undefined) return null;
    try {
      return JSON.parse(value);
    } catch (err) {
      return value;
    }
  };

  const formatValue = (fieldName, value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (fieldName === 'assigned_to') {
      return getDriDisplayName(value);
    }
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : '—';
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (err) {
        return String(value);
      }
    }
    return String(value);
  };

  const formatFieldName = (fieldName) => {
    if (!fieldName) return 'Change';
    return fieldName.replace(/_/g, ' ');
  };

  const loadHistory = useCallback(async () => {
    if (!apiService || !nodeId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getChangeHistory(nodeId);
      setChanges(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Failed to load change history:', err);
      setError(err.message || 'Failed to load change history.');
    } finally {
      setLoading(false);
    }
  }, [apiService, nodeId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleConfirmRevert = async () => {
    if (!confirmChange || !apiService) return;
    setRevertingId(confirmChange.id);
    try {
      await apiService.revertChange(confirmChange.id);
      setConfirmChange(null);
      await loadHistory();
      if (onRevert) {
        onRevert(confirmChange);
      }
    } catch (err) {
      console.error('Failed to revert change:', err);
      setError(err.message || 'Failed to revert change.');
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <div
      className="rounded-xl p-6 mt-6"
      style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
    >
      <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--pallium-text-primary)' }}>
        Change History
      </h2>

      {loading && (
        <div className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>
          Loading change history...
        </div>
      )}
      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ backgroundColor: 'var(--pallium-alert-error-bg)', color: 'var(--pallium-error-red)' }}
        >
          {error}
        </div>
      )}

      {!loading && changes.length === 0 && (
        <div className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>
          No changes recorded yet.
        </div>
      )}

      {!loading && changes.length > 0 && (
        <div className="space-y-3">
          {changes.map((change) => {
            const oldValue = parseValue(change.old_value);
            const newValue = parseValue(change.new_value);
            const fieldName = change.field_name || 'change';
            const isReverted = Boolean(change.reverted);
            const canRevert = Boolean(change.field_name) && !isReverted && change.change_type !== 'CREATE' && change.change_type !== 'DELETE' && change.change_type !== 'REVERT';
            return (
              <div
                key={change.id}
                className="p-3 rounded-lg"
                style={{
                  border: '1px solid var(--pallium-stroke-light)',
                  opacity: isReverted ? 0.6 : 1,
                }}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium" style={{ color: 'var(--pallium-text-primary)' }}>
                        {formatFieldName(fieldName)}
                      </span>
                      {change.change_type && (
                        <span
                          className="px-1.5 py-0.5 text-xs font-medium rounded"
                          style={{
                            backgroundColor: change.change_type === 'CREATE' ? 'rgba(16, 185, 129, 0.1)' :
                              change.change_type === 'DELETE' ? 'rgba(252, 57, 57, 0.1)' :
                              change.change_type === 'REVERT' ? 'rgba(245, 158, 11, 0.1)' :
                              'var(--pallium-purple-0-5)',
                            color: change.change_type === 'CREATE' ? '#059669' :
                              change.change_type === 'DELETE' ? 'var(--pallium-error-red)' :
                              change.change_type === 'REVERT' ? '#d97706' :
                              'var(--pallium-primary-purple)',
                          }}
                        >
                          {change.change_type}
                        </span>
                      )}
                      {isReverted && (
                        <span
                          className="px-1.5 py-0.5 text-xs font-medium rounded"
                          style={{ backgroundColor: 'rgba(156, 163, 175, 0.2)', color: 'var(--pallium-text-secondary)' }}
                        >
                          Reverted
                        </span>
                      )}
                    </div>
                    <div className="text-xs mt-1" style={{ color: 'var(--pallium-text-secondary)' }}>
                      {change.changed_by_user_email || 'Unknown'} · {formatDateTime(change.changed_at)}
                    </div>
                    {change.change_type !== 'CREATE' && change.change_type !== 'DELETE' && (
                      <div className="text-sm mt-2" style={{ color: 'var(--pallium-text-secondary)' }}>
                        <span style={{ color: 'var(--pallium-text-tertiary)' }}>From:</span>{' '}
                        {formatValue(fieldName, oldValue)}
                        {' '}→{' '}
                        <span style={{ color: 'var(--pallium-text-primary)' }}>
                          {formatValue(fieldName, newValue)}
                        </span>
                      </div>
                    )}
                  </div>
                  {canRevert && (
                    <button
                      onClick={() => setConfirmChange(change)}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg"
                      style={{
                        backgroundColor: 'var(--pallium-purple-0-5)',
                        color: 'var(--pallium-primary-purple)',
                      }}
                      disabled={revertingId === change.id}
                    >
                      {revertingId === change.id ? 'Reverting...' : 'Revert'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className="rounded-xl p-6 w-full max-w-md"
            style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-lg)' }}
          >
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--pallium-text-primary)' }}>
              Revert this change?
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
              This will set {formatFieldName(confirmChange.field_name)} back to its previous value.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmChange(null)}
                className="px-4 py-2 text-sm font-medium rounded-lg"
                style={{ color: 'var(--pallium-text-secondary)', backgroundColor: 'var(--pallium-bg-secondary)' }}
                disabled={revertingId === confirmChange.id}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRevert}
                className="px-4 py-2 text-sm font-medium rounded-lg text-white"
                style={{ backgroundColor: 'var(--pallium-primary-purple)' }}
                disabled={revertingId === confirmChange.id}
              >
                {revertingId === confirmChange.id ? 'Reverting...' : 'Revert'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeHistoryPanel;
